import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Link2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { DataTable, type Column } from '@/components/system/DataTable';
import {
  useIFConvites,
  useIFCriarConvite,
  useIFRevogarConvite,
  type IFInstituicao,
} from '../hooks/useIFAdminContratos';
import { format } from 'date-fns';

const statusBadge = (status: string) => {
  const map: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
    ativo: { variant: 'default', label: 'Ativo' },
    provisorio: { variant: 'secondary', label: 'Provisório' },
    pendente: { variant: 'outline', label: 'Pendente' },
    suspenso: { variant: 'secondary', label: 'Suspenso' },
    encerrado: { variant: 'outline', label: 'Encerrado' },
    aceito: { variant: 'default', label: 'Aceito' },
    revogado: { variant: 'destructive', label: 'Revogado' },
    expirado: { variant: 'outline', label: 'Expirado' },
  };
  const m = map[status] || { variant: 'outline' as const, label: status };
  return <Badge variant={m.variant} className="text-xs">{m.label}</Badge>;
};

const tipoVinculoBadge = (tipo: string) => {
  if (tipo === 'financiamento') return <Badge variant="default" className="text-xs">Financiamento</Badge>;
  return <Badge variant="outline" className="text-xs">Monitoramento</Badge>;
};

interface ConvitesIFProps {
  contratosDisponiveis: { id: string; nome: string; contratada?: string; status?: string }[];
  instituicoes: IFInstituicao[];
}

export const ConvitesIF: React.FC<ConvitesIFProps> = ({ contratosDisponiveis, instituicoes }) => {
  const { data: convites = [], isLoading } = useIFConvites();
  const criarConvite = useIFCriarConvite();
  const revogarConvite = useIFRevogarConvite();
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ contrato_id: '', tipo_acesso: 'monitoramento' as 'monitoramento' | 'financiamento', instituicao_destino_id: '' });
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!form.contrato_id) { toast.error('Selecione um contrato.'); return; }
    try {
      const result = await criarConvite.mutateAsync({
        contrato_id: form.contrato_id,
        tipo_acesso: form.tipo_acesso,
        instituicao_destino_id: form.instituicao_destino_id || null,
      });
      setGeneratedToken(result.token);
      toast.success('Convite gerado com sucesso.');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleCopyToken = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken);
      toast.success('Token copiado para a área de transferência.');
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await revogarConvite.mutateAsync(id);
      toast.success('Convite revogado.');
    } catch (err: any) { toast.error(err.message); }
  };

  const conviteColumns: Column<any>[] = [
    { key: 'contrato', label: 'Contrato', render: r => {
      const c = contratosDisponiveis.find((c: any) => c.id === r.contrato_id);
      return <span className="text-sm font-medium">{c?.nome || r.contrato_id.slice(0, 8)}</span>;
    }},
    { key: 'tipo', label: 'Tipo', align: 'center', render: r => tipoVinculoBadge(r.tipo_acesso) },
    { key: 'status', label: 'Status', align: 'center', render: r => statusBadge(r.status) },
    { key: 'expira', label: 'Expira em', render: r => <span className="text-sm text-muted-foreground">{format(new Date(r.expires_at), 'dd/MM/yyyy HH:mm')}</span> },
    { key: 'criado', label: 'Criado', render: r => <span className="text-sm text-muted-foreground">{format(new Date(r.created_at), 'dd/MM/yyyy')}</span> },
    { key: 'acoes', label: 'Ações', align: 'center', render: r => r.status === 'pendente' ? (
      <Button variant="ghost" size="sm" onClick={() => handleRevoke(r.id)} className="text-destructive">Revogar</Button>
    ) : null },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setShowDialog(true); setGeneratedToken(null); }} className="gap-2" size="sm">
          <Link2 className="h-4 w-4" /> Gerar Convite
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <DataTable
          columns={conviteColumns}
          data={convites}
          keyExtractor={r => r.id}
          emptyText="Nenhum convite gerado."
        />
      )}
      <Dialog open={showDialog} onOpenChange={v => { setShowDialog(v); if (!v) setGeneratedToken(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{generatedToken ? 'Convite Gerado' : 'Novo Convite de Acesso'}</DialogTitle></DialogHeader>
          {generatedToken ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Envie este token para a instituição financeira. Ele é de uso único e expira em 7 dias.</p>
              <div className="flex items-center gap-2">
                <Input value={generatedToken} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={handleCopyToken}><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Contrato</Label>
                <Select value={form.contrato_id} onValueChange={v => setForm(f => ({ ...f, contrato_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Selecione o contrato" /></SelectTrigger>
                  <SelectContent>
                    {contratosDisponiveis.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de Acesso</Label>
                <Select value={form.tipo_acesso} onValueChange={v => setForm(f => ({ ...f, tipo_acesso: v as any }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monitoramento">Monitoramento</SelectItem>
                    <SelectItem value="financiamento">Financiamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Instituição Destino (opcional)</Label>
                <Select value={form.instituicao_destino_id} onValueChange={v => setForm(f => ({ ...f, instituicao_destino_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Qualquer instituição" /></SelectTrigger>
                  <SelectContent>
                    {instituicoes.filter(i => i.ativo).map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); setGeneratedToken(null); }}>
              {generatedToken ? 'Fechar' : 'Cancelar'}
            </Button>
            {!generatedToken && (
              <Button onClick={handleCreate} disabled={criarConvite.isPending}>
                {criarConvite.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Gerar Token
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
